import React from 'react';
import classnames from 'classnames';
import S from './MomentHeader.css';
import Util from "./Util.js";
/**
 * Params
 * {projection} projection the projection used to create the header
 * {string} unit the unit used to calculate the group. 'day', 'month', etc
 * {string} format if present, moment.format( format ) will be used to create the label
 * {string|function} value if this is a function, it will be invoked with value( moment, width, left, right )
 * and the function should return a string | component for the label. If value is a string, moment[value]() will 
 * be invoked (ie 'date' will return the day of the month)
 * {string} className the className for the label
 * {string} className the className to apply to the header
 * {string} cellClassName the className to apply to each cell
 * @property {boolean} clip if true, the positions of the header cell will be clipped to the projection 
 * time. This is useful for left or right aligning labels like month
 * {boolean} sticky if true, the label component will be given sticky (left) css options
 * {string|React} component the component to create to hold all the cells. (default 'div')
 * {object} componentProps extra props to give to the component
 * {string|React} cellComponent the component to create to hold all a label. (default 'div')
 * {object} cellComponentProps extra props to give to the cell
 * {string|React} labelComponent the component to create for the labels. (default 'div')
 * {object} labelComponentProps extra props to give to the label
**/
class MomentHeader extends React.Component {
  render() {
    const {
      component,
      componentProps,
      labelComponent,
      labelComponentProps,
      cellComponent,
      cellComponentProps,
      projection,
      unit,
      value,
      clip = true,
      sticky,
      className,
      cellClassName,
      labelClassName,
      format,
      fillWidth,
      reverse = false,
      timezone
    } = this.props;
    const {
      startValue: startTime,
      stopValue: stopTime
    } = projection;
    const hWidth = projection.startToScreen(startTime) - projection.startToScreen(stopTime);
    const lbl = [];
    let startDateTime = Util.parseDateTime(startTime, timezone).startOf(unit);
    const stopDateTime = Util.parseDateTime(stopTime, timezone);
    let t = startDateTime.valueOf();
    const left = projection.startToScreen(t);
    const cn = classnames(S.headerCell, cellClassName);
    const lcn = classnames(labelClassName, sticky && S.sticky);
    let name = null;
    while (t < stopDateTime) {
      const left = projection.startToScreen(clip ? Math.max(startTime, t) : t);
      const curr = startDateTime;
      startDateTime = startDateTime.plus({
        [unit]: 1
      });
      t = startDateTime.valueOf();
      const right = projection.stopToScreen(clip ? Math.min(stopTime, t) : t);
      const width = right - left;
      const style = {
        width: `${width}px`
      };
      if (sticky) {
        style.overflow = 'visible !important';
      }
      const cStyle = {
        maxWidth: style.width,
        display: 'inline-block'
      };
      if (format) {
        name = curr.toFormat(format);
      } else if (typeof value === 'string') {
        name = curr[value];
      } else {
        name = value(curr, width, left, right);
      }
      lbl.push(/*#__PURE__*/React.createElement(cellComponent || 'div', {
        ...cellComponentProps,
        style,
        className: cn,
        key: `mh-${t}`
      }, /*#__PURE__*/React.createElement(labelComponent || 'div', {
        ...labelComponentProps,
        style: cStyle,
        className: lcn
      }, name)));
    }
    const items = reverse ? lbl.reverse() : lbl;
    if (fillWidth) {
      items.push(/*#__PURE__*/React.createElement(cellComponent || 'div', {
        ...cellComponentProps,
        style: {
          flexGrow: 1
        },
        className: cn,
        key: 'mh-fill'
      }));
    }
    return /*#__PURE__*/React.createElement(component || 'div', {
      ...componentProps,
      className: classnames(S.header, className),
      style: clip ? null : {
        left: `${left}px`,
        width: `${hWidth}px`
      }
    }, items);
  }
}
export default MomentHeader;